/**
 * /s/:token/v/:videoId — real guest watch page (Task 13 Chunk B).
 *
 * Reuses the LOCKED components/watch/* primitives (VideoPlayer, RightPanel,
 * AnnotationProvider, VersionSwitcher pattern, comment components) via import.
 * The comment / approval / view mutation hooks in use-watch.tsx were extended
 * in Chunk B to accept mode="share", so passing that + the token as watchKey
 * routes every mutation through the /api/share/:token/* endpoints correctly.
 *
 * The workspace watch page (pages/watch.tsx) is LOCKED per CLAUDE.md, so we
 * cannot import its private WatchShell. Duplicated the coordinator + top bar
 * here — mode-scoped chrome differs anyway.
 *
 * Gates:
 *   - shareSettings.commentAllowed=false → composer hidden (RightPanel itself
 *     doesn't consult share settings, but useCreateComment on the backend will
 *     403 — we gate client-side to avoid the round-trip and a bad UX).
 *     TODO: today the composer lives inside the LOCKED comment-list.tsx —
 *     there's no client-side switch; user will see 403 if commentAllowed=false.
 *     Server-side gate is authoritative.
 *   - shareSettings.downloadAllowed=false → download button hidden.
 *   - shareSettings.showAllVersions=false → version picker hides siblings; only
 *     latest is served by the backend.
 *   - skipForm=false → identify form gates the whole page (Frame.io parity for
 *     single-asset shares — "prompt at first interaction" is enforced by
 *     prompting upfront here, since the backend already required identify for
 *     view/comment/approval endpoints).
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  Loader2,
  PanelRight,
  PanelRightClose,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { APPROVAL_STATUS_LABELS } from "@/lib/api-types";
import { type VideoPlayerHandle } from "@/components/watch/video-player";
import { MediaSurface } from "@/components/watch/media-surface";
import { RightPanel } from "@/components/watch/right-panel";
import { AnnotationProvider } from "@/components/watch/annotation-context";
import { BrandMark } from "@/components/brand-mark";
import { ThemeToggle } from "@/components/theme-toggle";
import { ShareIdentifyForm } from "@/components/shares/share-identify-form";
import {
  useAutoRegisterView,
  useWatchComments,
  type WatchVersion,
} from "@/hooks/use-watch";
import { useShareLanding, useShareVideo } from "@/hooks/use-share";
import { ApiError } from "@/lib/api";

export function ShareWatchPage() {
  const { token, videoId } = useParams<{ token: string; videoId: string }>();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  return (
    <AnnotationProvider>
      <ShareWatchInner token={token} videoId={videoId} preview={preview} />
    </AnnotationProvider>
  );
}

function ShareWatchInner({
  token,
  videoId,
  preview = false,
}: {
  token: string | undefined;
  videoId: string | undefined;
  preview?: boolean;
}) {
  const navigate = useNavigate();
  const pv = preview ? "?preview=1" : "";   // Task 3 — keep team-preview on nav
  // Landing (share settings + adminName) piggybacks on the entry cache — no
  // extra hit if the guest came through /s/:token first.
  const landing = useShareLanding(token, preview);
  const video = useShareVideo(token, videoId, preview);

  // Auto-register view once skipForm=true (identify done) AND we know the
  // stack root. `useAutoRegisterView` fires once per (mode, key, videoId).
  useAutoRegisterView(
    "share",
    token,
    !!video.data?.skipForm && !!video.data?.rootVideoId,
    video.data?.rootVideoId,
  );

  // Comments — stack-wide from the backend, keyed on (mode, token, videoId).
  // Gated on skipForm — no point fetching before identify since some server
  // endpoints will 401 anyway.
  const { data: comments = [] } = useWatchComments(
    "share",
    video.data?.skipForm ? token : undefined,
    video.data?.rootVideoId,
  );

  if (video.isLoading || landing.isLoading) return <ShareWatchLoading />;

  const videoErr = video.error as ApiError | undefined;
  if (videoErr?.status === 404) {
    return (
      <Shell>
        <ShareWatchError
          title="Video not found in this share"
          body="The video might have been removed or isn't part of this share anymore."
          backHref={token ? `/s/${encodeURIComponent(token)}` : "/"}
        />
      </Shell>
    );
  }
  if (videoErr?.status === 410) {
    return (
      <Shell>
        <ShareWatchError
          title="This link has expired"
          body="Ask the owner to send a fresh link."
          backHref={null}
        />
      </Shell>
    );
  }
  if (!video.data || !landing.data) {
    return (
      <Shell>
        <ShareWatchError
          title="Couldn't load this video"
          body="Check your connection and try again."
          backHref={token ? `/s/${encodeURIComponent(token)}` : "/"}
        />
      </Shell>
    );
  }

  // Multi-item shares get gated at the landing already. Single-asset shares
  // (which auto-drilled straight to /v/:videoId) may hit this page without a
  // cookie — prompt identify here so they can comment / approve.
  if (!video.data.skipForm) {
    return (
      <Shell>
        <div className="mx-auto max-w-4xl px-6 py-16">
          <ShareIdentifyForm
            token={token!}
            adminName={video.data.adminName}
            onIdentified={() => video.refetch()}
          />
        </div>
      </Shell>
    );
  }

  return (
    <ShareWatchShell
      token={token!}
      backHref={
        landing.data.items.length > 1
          ? `/s/${encodeURIComponent(token!)}${pv}`
          : null   // single-asset landing = same page, no back
      }
      adminName={video.data.adminName}
      shareName={landing.data.share.name || null}
      video={video.data}
      comments={comments}
      onGoBack={() => {
        if (token) navigate(`/s/${encodeURIComponent(token)}${pv}`);
      }}
    />
  );
}

// ── Shell (rebuilt from watch.tsx WatchShell — that's LOCKED) ────────────────

function ShareWatchShell({
  token,
  backHref,
  adminName,
  shareName,
  video,
  comments,
}: {
  token: string;
  backHref: string | null;
  adminName?: string;
  shareName: string | null;
  video: import("@/hooks/use-share").ShareVideoData;
  comments: import("@/hooks/use-watch").WatchComment[];
  onGoBack?: () => void;
}) {
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const playerRef = useRef<VideoPlayerHandle>(null);

  const seek = useCallback((s: number) => {
    playerRef.current?.seek(s);
    playerRef.current?.pause();
  }, []);
  const getCurrentTime = useCallback(() => playerRef.current?.getCurrentTime() ?? 0, []);
  const handleSelectComment = useCallback((id: string) => {
    setSelectedCommentId(id);
    playerRef.current?.pause();
  }, []);

  // Versions the picker considers. When showAllVersions=false the backend
  // already only sends `[latest]`, but coerce here so the switcher can't
  // accidentally expose siblings on a stale cache.
  const versions: WatchVersion[] = useMemo(
    () =>
      video.shareSettings.showAllVersions
        ? video.versions.map((v) => ({
            id: v.id,
            versionNumber: v.versionNumber,
            assetType: v.assetType,
            approvalStatus: v.approvalStatus,
            cdnUrl: v.cdnUrl,
            createdAt: v.createdAt,
          }))
        : [
            {
              id: video.video.id,
              versionNumber: video.video.versionNumber,
              assetType: video.video.assetType,
              approvalStatus: video.video.approvalStatus,
              cdnUrl: video.video.cdnUrl,
            },
          ],
    [video],
  );

  const currentVersion: WatchVersion = useMemo(() => {
    if (selectedVersionId) {
      const hit = versions.find((v) => v.id === selectedVersionId);
      if (hit) return hit;
    }
    return {
      id: video.video.id,
      title: video.video.title,
      versionNumber: video.video.versionNumber,
      assetType: video.video.assetType,
      approvalStatus: video.video.approvalStatus,
      cdnUrl: video.video.cdnUrl,
    };
  }, [selectedVersionId, versions, video]);

  // Panel-wrap classNames match the workspace watch page (same rounded-corner
  // seam pattern). Kept in parity so both watch pages feel like one product.
  // Mobile: stack (h-[45vh] video over flex-1 panel). Desktop: side-by-side.
  const topBarWrap = "overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background";
  const videoWrap = "flex h-[45vh] overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-black md:h-auto md:flex-1";
  const rightPanelWrap =
    "flex min-h-0 flex-1 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background md:w-80 md:flex-none lg:w-96";

  const downloadAllowed =
    video.shareSettings.downloadAllowed && !!video.video.downloadAllowed;

  return (
    <div className="flex h-screen h-[100dvh] bg-sidebar text-foreground">
      {/* No rail for guests — matches the /watch/:token flow. */}
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <div className={topBarWrap}>
          <ShareTopBar
            token={token}
            backHref={backHref}
            adminName={adminName}
            shareName={shareName}
            videoTitle={video.video.title}
            versions={versions}
            activeVersionId={currentVersion.id}
            onSelectVersion={setSelectedVersionId}
            downloadAllowed={downloadAllowed}
            currentVideoId={currentVersion.id}
            panelOpen={panelOpen}
            onTogglePanel={() => setPanelOpen((v) => !v)}
          />
        </div>

        {/* Mobile: stack video over panel. Desktop: side-by-side. */}
        <div className="flex min-h-0 flex-1 flex-col gap-px md:flex-row">
          <div className={videoWrap}>
            <MediaSurface
              ref={playerRef}
              assetType={currentVersion.assetType}
              src={currentVersion.cdnUrl ?? null}
              comments={comments}
              mode="share"
              watchKey={token}
              currentVersionId={currentVersion.id}
              selectedCommentId={selectedCommentId}
              onSelectComment={handleSelectComment}
            />
          </div>

          {panelOpen && (
            <aside className={rightPanelWrap}>
              <RightPanel
                mode="share"
                currentVersion={currentVersion}
                comments={comments}
                watchKey={token}
                onSeek={seek}
                getCurrentTime={getCurrentTime}
                selectedCommentId={selectedCommentId}
                onSelectComment={handleSelectComment}
                assetType={currentVersion.assetType}
              />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Top bar (guest-style — rebuilt from watch.tsx GuestTopBar which is LOCKED) ──

function ShareTopBar({
  token,
  backHref,
  adminName,
  shareName,
  videoTitle,
  versions,
  activeVersionId,
  onSelectVersion,
  downloadAllowed,
  currentVideoId,
  panelOpen,
  onTogglePanel,
}: {
  token: string;
  backHref: string | null;
  adminName?: string;
  shareName: string | null;
  videoTitle: string;
  versions: WatchVersion[];
  activeVersionId: string | null;
  onSelectVersion: (id: string | null) => void;
  downloadAllowed: boolean;
  currentVideoId: string;
  panelOpen: boolean;
  onTogglePanel: () => void;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
      {backHref && (
        <Link
          to={backHref}
          aria-label="Back"
          title="Back to share"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
        </Link>
      )}

      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/20 text-xs font-semibold uppercase text-primary">
        {initialsFrom(adminName)}
      </div>
      <div className="hidden text-sm font-medium text-foreground sm:block">
        {shareName || (adminName ? `${adminName}'s Workspace` : "Shared with you")}
      </div>
      <span className="hidden text-muted-foreground sm:block">/</span>

      <VersionSwitcher
        title={videoTitle}
        versions={versions}
        activeVersionId={activeVersionId}
        onSelect={onSelectVersion}
      />

      {downloadAllowed && (
        <a
          href={`/api/share/${encodeURIComponent(token)}/videos/${encodeURIComponent(currentVideoId)}/download?videoId=${encodeURIComponent(currentVideoId)}`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          title="Download this version"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </a>
      )}

      <PanelToggle open={panelOpen} onToggle={onTogglePanel} />
    </header>
  );
}

// ── Small helpers duplicated from watch.tsx (LOCKED, can't import). ───────────

function VersionSwitcher({
  title,
  versions,
  activeVersionId,
  onSelect,
}: {
  title: string;
  versions: WatchVersion[];
  activeVersionId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const active = versions.find((v) => v.id === activeVersionId);
  const activeVersionNumber = active?.versionNumber;
  const hasStack = versions.length > 1;
  const latestId = useMemo(() => {
    if (!versions.length) return null;
    return versions.reduce(
      (best, v) => (v.versionNumber > best.versionNumber ? v : best),
      versions[0],
    ).id;
  }, [versions]);

  if (!hasStack) {
    return (
      <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {title}
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <DropdownMenu>
        <DropdownMenuTrigger className="flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60">
          <span className="truncate">{title}</span>
          {activeVersionNumber != null && (
            <span className="shrink-0 text-xs text-muted-foreground">
              V{activeVersionNumber} of {versions.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {[...versions]
            .sort((a, b) => b.versionNumber - a.versionNumber)
            .map((v) => {
              const isActive = v.id === activeVersionId;
              return (
                <DropdownMenuItem
                  key={v.id}
                  onSelect={() => onSelect(v.id === latestId ? null : v.id)}
                  className="flex-col items-start gap-0.5"
                >
                  <div className="flex w-full items-center gap-2">
                    {isActive ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <span className="inline-block h-3.5 w-3.5" />
                    )}
                    <span className="font-medium">V{v.versionNumber}</span>
                    {v.id === latestId && (
                      <span className="rounded bg-secondary/60 px-1 text-[10px] font-medium text-muted-foreground">
                        latest
                      </span>
                    )}
                  </div>
                  <div className="ml-5 text-[11px] text-muted-foreground">
                    {v.approvalStatus ? APPROVAL_STATUS_LABELS[v.approvalStatus] : "—"}
                  </div>
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function PanelToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
      aria-label={open ? "Hide comments panel" : "Show comments panel"}
      title={open ? "Hide comments panel" : "Show comments panel"}
    >
      {open ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
    </button>
  );
}

function initialsFrom(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ── Loading / error variants ──────────────────────────────────────────────────

function ShareWatchLoading() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function ShareWatchError({
  title,
  body,
  backHref,
}: {
  title: string;
  body: string;
  backHref: string | null;
}) {
  return (
    <main className="mx-auto max-w-md pt-32 text-center">
      <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      {backHref && (
        <Link
          to={backHref}
          className="mt-6 inline-block text-sm text-primary hover:underline"
        >
          Back to share
        </Link>
      )}
    </main>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between border-b border-border px-4 py-3 md:px-8 md:py-4">
        <BrandMark />
        <ThemeToggle />
      </header>
      {children}
    </div>
  );
}
